import dynamic from 'next/dynamic';

import DrupalImage from '@commons/components/Image/Drupal';
import { MediaQuery } from '@commons/contexts/responsive';
import type { DrupalBlock, DrupalImage as DrupalImageType } from '@commons/types/drupal';
import type { Theme } from '@commons/types/styles';

import type { Block } from '..';

/**
 * Lazy loading to reduce the initial bundle size.
 *
 * This does not work when used in a pagebuilder factory directly, because every items are loaded at once.
 * Doing it in the component itself ensures that the chunks are only loaded when the component is rendered.
 *
 * @see https://nextjs.org/docs/app/building-your-application/optimizing/lazy-loading
 */

const ImageReveal = dynamic(() => import('./ImageReveal'));
const MobileFallback = dynamic(() => import('./MobileFallback'));

// Type definition for the ImageRevealBlock component properties.

export type ImageRevealProps = DrupalBlock<{
  body: string;
  media: DrupalImageType;
  zoomLevel: string;
  theme: Theme;
}>;

/**
 * ImageRevealBlock component.
 */

const ImageRevealBlock: Block<ImageRevealProps> = (props) => {
  const renderedFallback = <DrupalImage {...props.content.media} />;

  return (
    <>
      <MediaQuery min="lg" fallback={renderedFallback}>
        <ImageReveal {...props} />
      </MediaQuery>
      <MediaQuery max="md">
        <MobileFallback {...props} />
      </MediaQuery>
    </>
  );
};

/**
 * This block uses GSAP ScrollTrigger, so we disable the default animation.
 */

ImageRevealBlock.supportsAnimation = false;

export default ImageRevealBlock;
